/**
 * 验证/测试工具函数
 */

/**
 * 验证电子邮箱格式
 * @param value 邮箱地址
 * @returns 是否为有效邮箱
 */
function email(value: string): boolean {
  return /^\w+((-\w+)|(\.\w+))*\@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z0-9]+$/.test(value);
}

/**
 * 验证手机格式
 * @param value 手机号
 * @returns 是否为有效手机号
 */
function mobile(value: string): boolean {
  return /^1[23456789]\d{9}$/.test(value);
}

/**
 * 验证 URL 格式
 * @param value URL 地址
 * @returns 是否为有效 URL
 */
function url(value: string): boolean {
  return /^((https|http|ftp|rtsp|mms):\/\/)(([0-9a-zA-Z_!~*'().&=+$%-]+: )?[0-9a-zA-Z_!~*'().&=+$%-]+@)?(([0-9]{1,3}.){3}[0-9]{1,3}|([0-9a-zA-Z_!~*'()-]+.)*([0-9a-zA-Z][0-9a-zA-Z-]{0,61})?[0-9a-zA-Z].[a-zA-Z]{2,6})(:[0-9]{1,4})?((\/?)|(\/[0-9a-zA-Z_!~*'().;?:@&=+$,%#-]+)+\/?)$/.test(
    value,
  );
}

/**
 * 验证日期格式
 * @param value 日期值
 * @returns 是否为有效日期
 */
function date(value: any): boolean {
  if (!value) return false;
  // 判断是否数值或者字符串数值(意味着为时间戳)，转为数值，否则 new Date 无法识别字符串时间戳
  if (number(value)) value = +value;
  return !/Invalid|NaN/.test(new Date(value).toString());
}

/**
 * 验证 ISO 类型的日期格式
 * @param value 日期字符串
 * @returns 是否为有效 ISO 日期格式
 */
function dateISO(value: string): boolean {
  return /^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])$/.test(value);
}

/**
 * 验证十进制数字
 * @param value 数值
 * @returns 是否为有效数字
 */
function number(value: any): boolean {
  return /^[\+-]?(\d+\.?\d*|\.\d+|\d\.\d+e\+\d+)$/.test(value);
}

/**
 * 验证字符串
 * @param value 值
 * @returns 是否为字符串
 */
function string(value: any): boolean {
  return typeof value === 'string';
}

/**
 * 验证整数
 * @param value 值
 * @returns 是否为整数
 */
function digits(value: string | number): boolean {
  return /^\d+$/.test(String(value));
}

/**
 * 验证身份证号码
 * @param value 身份证号
 * @returns 是否为有效身份证号
 */
function idCard(value: string): boolean {
  return /^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}([0-9]|X)$/.test(value);
}

/**
 * 是否车牌号
 * @param value 车牌号
 * @returns 是否为有效车牌号
 */
function carNo(value: string): boolean {
  // 新能源车牌
  const xreg =
    /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领A-Z]{1}[A-Z]{1}(([0-9]{5}[DF]$)|([DF][A-HJ-NP-Z0-9][0-9]{4}$))/;
  // 旧车牌
  const creg =
    /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领A-Z]{1}[A-Z]{1}[A-HJ-NP-Z0-9]{4}[A-HJ-NP-Z0-9挂学警港澳]{1}$/;
  if (value.length === 7) {
    return creg.test(value);
  }
  if (value.length === 8) {
    return xreg.test(value);
  }
  return false;
}

/**
 * 金额，只允许 2 位小数
 * @param value 金额值
 * @returns 是否为有效金额格式
 */
function amount(value: string): boolean {
  return /^[1-9]\d*(,\d{3})*(\.\d{1,2})?$|^0\.\d{1,2}$/.test(value);
}

/**
 * 中文
 * @param value 值
 * @returns 是否全为中文
 */
function chinese(value: string): boolean {
  const reg = /^[\u4e00-\u9fa5]+$/gi;
  return reg.test(value);
}

/**
 * 只能输入字母
 * @param value 值
 * @returns 是否全为字母
 */
function letter(value: string): boolean {
  return /^[a-zA-Z]*$/.test(value);
}

/**
 * 只能是字母或者数字
 * @param value 值
 * @returns 是否全为字母或数字
 */
function enOrNum(value: string): boolean {
  const reg = /^[0-9a-zA-Z]*$/g;
  return reg.test(value);
}

/**
 * 验证是否包含某个值
 * @param value 原字符串
 * @param param 要查找的字符串
 * @returns 是否包含
 */
function contains(value: string, param: string): boolean {
  return value.indexOf(param) >= 0;
}

/**
 * 验证一个值范围 [min, max]
 * @param value 值
 * @param param 范围数组 [min, max]
 * @returns 是否在范围内
 */
function range(value: number, param: [number, number]): boolean {
  return value >= param[0] && value <= param[1];
}

/**
 * 验证一个长度范围 [min, max]
 * @param value 字符串或数组
 * @param param 长度范围 [min, max]
 * @returns 长度是否在范围内
 */
function rangeLength(value: string | any[], param: [number, number]): boolean {
  return value.length >= param[0] && value.length <= param[1];
}

/**
 * 是否固定电话
 * @param value 电话号码
 * @returns 是否为有效固定电话
 */
function landline(value: string): boolean {
  const reg = /^\d{3,4}-\d{7,8}(-\d{3,4})?$/;
  return reg.test(value);
}

/**
 * 判断是否为空
 * @param value 值
 * @returns 是否为空
 */
function empty(value: any): boolean {
  switch (typeof value) {
    case 'undefined':
      return true;
    case 'string':
      if (value.replace(/(^[ \t\n\r]*)|([ \t\n\r]*$)/g, '').length == 0) return true;
      break;
    case 'boolean':
      if (!value) return true;
      break;
    case 'number':
      if (value === 0 || isNaN(value)) return true;
      break;
    case 'object':
      if (value === null || (value as any).length === 0) return true;
      for (const i in value) {
        return false;
      }
      return true;
  }
  return false;
}

/**
 * 是否 json 字符串
 * @param value 值
 * @returns 是否为 JSON 字符串
 */
function jsonString(value: string): boolean {
  if (typeof value === 'string') {
    try {
      const obj = JSON.parse(value);
      if (typeof obj === 'object' && obj) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }
  return false;
}

/**
 * 是否数组
 * @param value 值
 * @returns 是否为数组
 */
function array(value: any): boolean {
  if (typeof Array.isArray === 'function') {
    return Array.isArray(value);
  }
  return Object.prototype.toString.call(value) === '[object Array]';
}

/**
 * 是否对象
 * @param value 值
 * @returns 是否为对象
 */
function object(value: any): boolean {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * 是否短信验证码
 * @param value 验证码
 * @param len 长度，默认 6
 * @returns 是否为有效验证码格式
 */
function code(value: string | number, len: number = 6): boolean {
  return new RegExp(`^\\d{${len}}$`).test(String(value));
}

/**
 * 是否函数方法
 * @param value 值
 * @returns 是否为函数
 */
function func(value: any): boolean {
  return typeof value === 'function';
}

/**
 * 是否 promise 对象
 * @param value 值
 * @returns 是否为 Promise 对象
 */
function promise(value: any): boolean {
  return object(value) && func(value.then) && func(value.catch);
}

/**
 * 是否图片格式
 * @param value 文件路径或 URL
 * @returns 是否为图片格式
 */
function image(value: string): boolean {
  const newValue = value.split('?')[0];
  const IMAGE_REGEXP = /\.(jpeg|jpg|gif|png|svg|webp|jfif|bmp|dpg)/i;
  return IMAGE_REGEXP.test(newValue);
}

/**
 * 是否视频格式
 * @param value 文件路径或 URL
 * @returns 是否为视频格式
 */
function video(value: string): boolean {
  const VIDEO_REGEXP = /\.(mp4|mpg|mpeg|dat|asf|avi|rm|rmvb|mov|wmv|flv|mkv|m3u8)/i;
  return VIDEO_REGEXP.test(value);
}

/**
 * 是否为正则对象
 * @param o 值
 * @returns 是否为正则对象
 */
function regExp(o: any): boolean {
  return o && Object.prototype.toString.call(o) === '[object RegExp]';
}

/** 验证工具对象 */
const test = {
  email,
  mobile,
  url,
  date,
  dateISO,
  number,
  digits,
  idCard,
  carNo,
  amount,
  chinese,
  letter,
  enOrNum,
  contains,
  range,
  rangeLength,
  empty,
  isEmpty: empty,
  isNumber: number,
  jsonString,
  landline,
  object,
  array,
  code,
  func,
  promise,
  image,
  video,
  regExp,
};

export default test;
